import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ProviderSidebarComponent } from '../sidebar/provider-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AssetRequestComponent } from '../../shared/components/asset-request/asset-request.component';
import { CreatedPoliciesComponent } from '../../org-admin/access-requests/created-policies/created-policies.component';
import { AclApdApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';

@Component({
  selector: 'app-provider-access-requests',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ProviderSidebarComponent,
    PageHeaderComponent,
    AssetRequestComponent,
    CreatedPoliciesComponent,
  ],
  templateUrl: './access-requests.component.html',
  styleUrl: './access-requests.component.scss',
})
export class ProviderAccessRequestsComponent implements OnInit, AfterViewInit {
  @ViewChild(AssetRequestComponent) assetRequestComponent!: AssetRequestComponent;

  private readonly aclApdApiService = inject(AclApdApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly keycloak = inject(KeycloakService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  activeTab: 'assetRequest' | 'policies' = 'assetRequest';

  get pageTitle(): string {
    return this.activeTab === 'assetRequest' ? 'My Asset Requests' : 'Access Policy';
  }

  get pageDescription(): string {
    return this.activeTab === 'assetRequest'
      ? 'Review and manage asset access requests.'
      : 'Review and manage organisation access policies.';
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const tabFromUrl = params.get('tab');
      if (tabFromUrl === 'assetRequest' || tabFromUrl === 'policies' || tabFromUrl === 'createdPolicies') {
        const nextTab =
          tabFromUrl === 'createdPolicies' ? 'policies' : (tabFromUrl as 'assetRequest' | 'policies');
        if (this.activeTab !== nextTab) {
          this.activeTab = nextTab;
          if (nextTab === 'assetRequest') {
            void this.loadProviderAssetRequestsWhenReady();
          }
        }
        return;
      }

      // Default: My Asset Requests view, keep URL in sync for sidebar highlighting
      if (!tabFromUrl) {
        this.activeTab = 'assetRequest';
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: 'assetRequest' },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    await Promise.resolve();
    await this.waitForKeycloakReady();

    if (this.activeTab === 'assetRequest') {
      await this.loadProviderAssetRequestsWhenReady();
    }
  }

  private async loadProviderAssetRequestsWhenReady(): Promise<void> {
    for (let attempt = 0; attempt < 25; attempt++) {
      this.cdr.detectChanges();
      if (this.assetRequestComponent) {
        await this.loadProviderAssetRequests();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 16));
    }
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();

      if (keycloakInstance?.authenticated && keycloakInstance?.token) {
        return;
      }

      const maxWaitTime = 5000;
      const checkInterval = 100;
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    } catch {
      // Proceed — API calls will surface auth errors if needed.
    }
  }

  async loadProviderAssetRequests(): Promise<void> {
    try {
      if (!this.assetRequestComponent) {
        return;
      }

      const page = this.assetRequestComponent.pagination?.currentPage || 1;
      const size = this.assetRequestComponent.pagination?.pageSize || 10;
      const isExpired = this.assetRequestComponent.selectedRequestStatus === 'EXPIRED';
      const requestStatus = this.assetRequestComponent.getRequestStatusForApi();
      const explicitSort = this.assetRequestComponent.getSortQueryString()?.trim();
      const sortParam = explicitSort || 'createdAt:desc';

      const response = await this.aclApdApiService.providerAssetRequests(
        page,
        size,
        requestStatus,
        sortParam,
        isExpired,
      );

      this.assetRequestComponent.mapProviderAssetRequestsResponse(response);
      this.cdr.detectChanges();
    } catch {
      if (this.assetRequestComponent) {
        this.assetRequestComponent.loading = false;
      }
    }
  }
}
